import React, { useEffect } from 'react';
import { Box, Typography, Button, Container, Grid, Card, CardContent, CardMedia, Chip, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { ArrowForward as ArrowForwardIcon, AutoAwesome as SparklesIcon, TrendingUp as TechIcon } from '@mui/icons-material';
import { blogs } from '../data/blogs';
import FaqAccordion from '../components/FaqAccordion';

const Home = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;

  useEffect(() => {
    document.title = "CoreTechTalks | Insights & Tools for Modern Technology";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "CoreTechTalks is your essential source for expert analysis, practical tools, and in-depth guides on modern technology, software testing, AI tools, and data quality.");
    }
  }, []);

  return (
    <Box sx={{ flexGrow: 1, position: 'relative', overflow: 'hidden', pb: 10 }}>
      {/* Decorative Glowing Orbs */}
      <Box 
        className="glow-orb" 
        sx={{ 
          width: { xs: '250px', md: '500px' }, 
          height: { xs: '250px', md: '500px' }, 
          top: '-150px', 
          right: '-50px', 
          background: 'rgba(99, 102, 241, 0.15)',
        }} 
      />
      <Box 
        className="glow-orb" 
        sx={{ 
          width: { xs: '200px', md: '400px' }, 
          height: { xs: '200px', md: '400px' }, 
          bottom: '20%', 
          left: '-100px', 
          background: 'rgba(6, 182, 212, 0.1)',
        }} 
      />

      {/* Hero Section */}
      <Box sx={{ 
        py: { xs: 10, md: 16 }, 
        textAlign: 'center',
        position: 'relative',
        zIndex: 2
      }}>
        <Container maxWidth="md">
          {/* Badge */}
          <Chip 
            icon={<SparklesIcon sx={{ fontSize: '16px !important', color: mode === 'light' ? '#4F46E5' : '#818CF8' }} />}
            label="Version 2.0 Live - Intelligent Web Utilities"
            sx={{ 
              mb: 4, 
              py: 2.2,
              px: 1.5,
              fontSize: '13px', 
              fontWeight: 700,
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              color: mode === 'light' ? '#4F46E5' : '#C7D2FE',
              borderColor: mode === 'light' ? 'rgba(79, 70, 229, 0.2)' : 'rgba(129, 140, 248, 0.3)',
              background: mode === 'light' ? 'rgba(79, 70, 229, 0.05)' : 'rgba(129, 140, 248, 0.08)',
              borderWidth: '1.5px',
              borderStyle: 'solid'
            }}
          />

          <Typography 
            variant="h1" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontSize: { xs: '40px', sm: '56px', md: '68px' },
              lineHeight: 1.1,
              mb: 3
            }}
          >
            Insights That <span className="gradient-text">Build the Future</span>
          </Typography>
          
          <Typography 
            variant="h2" 
            sx={{ 
              color: 'text.secondary', 
              fontSize: { xs: '18px', md: '22px' }, 
              fontWeight: 500,
              lineHeight: 1.6,
              mb: 6, 
              maxWidth: '680px', 
              mx: 'auto' 
            }}
          >
            Your ultimate hub for expert technology articles, automated scraping toolchains, and deep AI document inspection software.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to="/tools"
              size="large"
              sx={{ px: 4, py: 1.6 }}
              endIcon={<ArrowForwardIcon />}
            >
              Launch Applications
            </Button>
            <Button
              variant="outlined"
              color="primary"
              component={Link}
              to="/blogs"
              size="large"
              sx={{ px: 4, py: 1.6 }}
            >
              Read Publications
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Main Apps Preview Section */}
      <Container maxWidth="lg" sx={{ py: 6, mb: 10, position: 'relative', zIndex: 2 }}>
        <Typography 
          variant="h3" 
          component="h2" 
          sx={{ mb: 1, textAlign: 'center', fontFamily: '"Outfit", sans-serif' }}
        >
          Our Core Applications
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ mb: 6, textAlign: 'center', maxWidth: '600px', mx: 'auto' }}
        >
          Access our customized AI analytics and document inspection tools built for scale.
        </Typography>

        <Grid container justifyContent="center">
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%', alignItems: 'center', textAlign: 'center' }}>
              <Box 
                sx={{ 
                  display: 'inline-flex',
                  p: 1.5,
                  borderRadius: '12px',
                  bgcolor: 'rgba(6, 182, 212, 0.1)',
                  color: mode === 'light' ? '#0891B2' : '#22D3EE',
                  mb: 3
                }}
              >
                <TechIcon sx={{ fontSize: '32px' }} />
              </Box>
              <Typography variant="h4" component="h3" sx={{ mb: 1.5, fontWeight: 700 }}>
                See Your Networth
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: '600px' }}>
                Track your investments, liabilities, and monthly expenses. Project your net worth and compound growth up to 40 years into the future with a private, secure dashboard.
              </Typography>
              <Button 
                href="https://www.seeyournetworth.com/"
                target="_blank"
                rel="noopener noreferrer"
                variant="contained" 
                color="primary"
                sx={{ px: 4, py: 1.2 }}
              >
                Launch Tracker
              </Button>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Featured Articles Section */}
      <Container maxWidth="lg" sx={{ py: 6, mb: 4, position: 'relative', zIndex: 2 }}>
        <Typography 
          variant="h3" 
          component="h2" 
          sx={{ mb: 1, textAlign: 'center', fontFamily: '"Outfit", sans-serif' }}
        >
          Featured Articles
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ mb: 6, textAlign: 'center', maxWidth: '600px', mx: 'auto' }}
        >
          Deep dives on software architecture, scraping patterns, and the latest web technologies.
        </Typography>

        <Grid container spacing={4}>
          {blogs.slice(0, 3).map((blog, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                component={Link} 
                to={`/blogs/${blog.slug}`} 
                sx={{ 
                  textDecoration: 'none', 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ position: 'relative', overflow: 'hidden', pt: '56.25%' }}>
                  <CardMedia
                    component="img"
                    image={blog.image}
                    alt={blog.title}
                    sx={{ 
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.5s ease',
                      '&:hover': {
                        transform: 'scale(1.08)'
                      }
                    }}
                  />
                  <Chip 
                    label={blog.category} 
                    size="small"
                    sx={{ 
                      position: 'absolute', 
                      top: '16px', 
                      left: '16px',
                      fontWeight: 700,
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      bgcolor: mode === 'light' ? 'rgba(255,255,255,0.9)' : 'rgba(17, 22, 37, 0.9)',
                      color: 'primary.main',
                      backdropFilter: 'blur(4px)',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}
                  />
                </Box>
                
                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h5" component="h3" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5, lineHeight: 1.4 }}>
                    {blog.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                    {blog.excerpt}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', pt: 2, borderTop: `1px solid ${mode === 'light' ? '#F1F5F9' : 'rgba(255,255,255,0.06)'}` }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TechIcon sx={{ fontSize: '14px' }} />
                      {blog.readTime}
                    </Typography>
                    <Typography variant="subtitle2" color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 700 }}>
                      Read Info <ArrowForwardIcon sx={{ fontSize: '16px' }} />
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FAQs Section */}
      <FaqAccordion />
    </Box>
  );
};

export default Home;
